/**
 * The prose half of a proposal.
 *
 * TipTap rather than a contenteditable of our own, for one reason: what gets
 * stored is a document tree the editor's schema defines, not a string of
 * markup. A hand-rolled editor stores whatever the browser produced and hands
 * the rendering side an HTML string it has to trust — which is a sanitiser to
 * write, and an XSS hole the first time it misses something. Here the only
 * nodes that can exist are the ones StarterKit declares, and rendering never
 * goes near `dangerouslySetInnerHTML`.
 *
 * The toolbar is deliberately short. A proposal wants headings, emphasis and
 * lists; it does not want font pickers, and every control that isn't there is
 * one that can't put something in a client's document that the print layout
 * has no style for.
 */
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";

/**
 * The stored shape. Matches `proseDoc` in the API's schemas.
 *
 * The `type: "doc"` literal is narrower than TipTap's own `JSONContent`, whose
 * `type` is an optional string — the root of a document TipTap produces is
 * always a doc, but its type doesn't say so, and the API's schema does.
 */
export type ProseDoc = JSONContent & { type: "doc" };

export const EMPTY_DOC: ProseDoc = { type: "doc", content: [{ type: "paragraph" }] };

/**
 * The shared typography. Applied to both the editor surface and the read-only
 * renderer, so what you type is positioned exactly where it prints — a
 * proposal that reflows when you stop editing is one nobody trusts.
 */
const PROSE =
  "text-sm leading-relaxed text-neutral-700 " +
  "[&_p]:my-2 " +
  "[&_h1]:mt-5 [&_h1]:mb-2 [&_h1]:font-[family-name:--font-display] [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-neutral-900 " +
  "[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:font-[family-name:--font-display] [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-neutral-900 " +
  "[&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:text-neutral-900 " +
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 " +
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 " +
  "[&_li]:my-0.5 " +
  "[&_strong]:font-semibold [&_em]:italic " +
  "[&_blockquote]:border-l-2 [&_blockquote]:border-neutral-300 [&_blockquote]:pl-3 [&_blockquote]:text-neutral-600";

const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    // A proposal has no use for either, and both would print as something the
    // client's copy has no styling for.
    codeBlock: false,
    horizontalRule: false,
  }),
];

interface ToolButton {
  label: string;
  title: string;
  isActive: (e: NonNullable<ReturnType<typeof useEditor>>) => boolean;
  run: (e: NonNullable<ReturnType<typeof useEditor>>) => void;
}

const TOOLS: ToolButton[] = [
  {
    label: "B",
    title: "Bold",
    isActive: (e) => e.isActive("bold"),
    run: (e) => e.chain().focus().toggleBold().run(),
  },
  {
    label: "I",
    title: "Italic",
    isActive: (e) => e.isActive("italic"),
    run: (e) => e.chain().focus().toggleItalic().run(),
  },
  {
    label: "H2",
    title: "Heading",
    isActive: (e) => e.isActive("heading", { level: 2 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: "H3",
    title: "Subheading",
    isActive: (e) => e.isActive("heading", { level: 3 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    label: "• List",
    title: "Bulleted list",
    isActive: (e) => e.isActive("bulletList"),
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    label: "1. List",
    title: "Numbered list",
    isActive: (e) => e.isActive("orderedList"),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
];

/**
 * An editable prose block.
 *
 * `onChange` fires on every keystroke and the caller holds the document in
 * state — saving is an explicit act on the page, not an autosave per
 * character. A proposal is a document someone is composing; a save on every
 * letter would write a hundred versions of a sentence that was never finished.
 */
export function RichText({
  value,
  onChange,
  editable = true,
  placeholder,
}: {
  value: ProseDoc;
  onChange: (doc: ProseDoc) => void;
  editable?: boolean;
  placeholder?: string;
}) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value,
    editable,
    editorProps: {
      attributes: {
        class: `${PROSE} min-h-24 rounded-lg px-3 py-2 outline-none focus:bg-white`,
        ...(placeholder ? { "aria-label": placeholder } : {}),
      },
    },
    // `getJSON` returns the whole document, whose root node is always a doc —
    // the cast only tells the compiler what the editor already guarantees.
    onUpdate: ({ editor: e }) => onChange(e.getJSON() as ProseDoc),
  });

  // `setEditable` emits an `update` event unless the second argument says not
  // to, and this effect runs the moment the editor exists — on every mount.
  // Left to emit, it hands the caller a document nobody typed into, which the
  // proposal editor above reads as an unsaved change: the page loads dirty,
  // and Approve sits disabled behind "Save your changes first" that saving can
  // never clear, because a save rehydrates the drafts under fresh keys, which
  // remounts these editors and fires the whole thing again.
  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editor, editable]);

  if (!editor) return null;

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 transition-colors focus-within:border-accent-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-accent-500/20">
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-neutral-200 px-1.5 py-1">
          {TOOLS.map((tool) => (
            <button
              key={tool.label}
              type="button"
              title={tool.title}
              onClick={() => tool.run(editor)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                tool.isActive(editor)
                  ? "bg-accent-100 text-accent-800"
                  : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
              }`}
            >
              {tool.label}
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

/**
 * The same document, rendered for reading — the client's view, and what the
 * page prints.
 *
 * Runs TipTap in non-editable mode rather than converting to HTML, so the
 * printed document and the edited one come out of the same schema and the same
 * stylesheet. Converting would mean a second renderer to keep in step, and the
 * first divergence would be silent.
 */
export function RichTextView({ value }: { value: ProseDoc }) {
  const editor = useEditor(
    {
      extensions: EXTENSIONS,
      content: value,
      editable: false,
      editorProps: { attributes: { class: PROSE } },
    },
    // Re-render when the stored document changes: a saved edit has to show up
    // in the preview beside it without a reload.
    [value],
  );

  if (!editor) return null;
  return <EditorContent editor={editor} />;
}
